/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal/Wizard/MetadataActionCreateModalWizardStepRelationType
 * @desc Step 2: Relation Type - Select the type of relationship (one_way, symmetric, asymmetric)
 */

import { FieldDefinitionFormLinkTypePreview } from 'labstep-web/components/FieldDefinition/Form/LinkTypePreview';
import { FieldDefinitionFormRelationTypeButtonContent } from 'labstep-web/components/FieldDefinition/Form/RelationTypeButtonContent';
import { CardSelect } from 'labstep-web/core/Card/Select';
import { Flex } from 'labstep-web/core/Flex';
import type { IValues } from 'labstep-web/core/Form/Reusable/types';
import { EntityRelationType } from 'labstep-web/models/field-definition.model';
import { Resource } from 'labstep-web/models/resource.model';
import { startCase } from 'lodash';
import type React from 'react';

interface MetadataActionCreateModalWizardStepRelationTypeProps {
  formValues: IValues;
  onFormChange: (values: IValues) => void;
  isTemplate: boolean;
}

export const MetadataActionCreateModalWizardStepRelationType: React.FC<
  MetadataActionCreateModalWizardStepRelationTypeProps
> = ({ formValues, onFormChange, isTemplate }) => {
  // Only disable options if isTemplate is true and no template is selected
  let isDisabled = false;
  let template_id = null;
  if (isTemplate) {
    template_id =
      (formValues?.entity_relation_target_entity_filter_template_id as any) ||
      (
        formValues?.entity_relation_target_entity_filter_template as any
      )?.id ||
      (
        formValues?.entity_relation_target_entity_filter_template as any
      )?.guid;
    isDisabled = template_id === undefined || template_id === null;
  }

  // Get the selected relation type
  const selectedRelationType =
    formValues.entity_relation_type as EntityRelationType;

  // Define example data based on relation type
  const getExampleData = () => {
    switch (selectedRelationType) {
      case EntityRelationType.one_way:
        return {
          categoryA: 'Sample',
          categoryB: 'Reagent',
          relationName: 'Solvent',
          backLinkName: '',
          resourceNameA: 'Blood Sample #1',
          resourceNameB: 'DMSO',
        };
      case EntityRelationType.symmetric:
        return {
          categoryA: 'Antibody',
          categoryB: 'Antibody',
          relationName: 'Alternatives',
          backLinkName: '',
          resourceNameA: 'Anti-GFP (Goat)',
          resourceNameB: 'Anti-GFP (Rabbit)',
        };
      case EntityRelationType.asymmetric:
        return {
          categoryA: 'Cell Line',
          categoryB: 'Cell Line',
          relationName: 'Children',
          backLinkName: 'Parent',
          resourceNameA: 'HEK293T Parent',
          resourceNameB: 'HEK293T Clone A',
        };
      default:
        return {
          categoryA: 'Sample',
          categoryB: 'Reagent',
          relationName: 'contains',
          backLinkName: '',
          resourceNameA: 'Blood Sample #1',
          resourceNameB: 'DMSO',
        };
    }
  };

  const exampleData = getExampleData();

  return (
    <>
      <Flex spacing="gap">
        {Object.values(EntityRelationType).map(
          (entityRelationType) => {
            const isOptionDisabled =
              isTemplate &&
              isDisabled &&
              entityRelationType !== EntityRelationType.one_way;

            return (
              <Flex
                key={entityRelationType}
                style={{ marginTop: '10px' }}
              >
                <CardSelect
                  data-testid={`card-select-${entityRelationType}`}
                  isSelected={
                    formValues.entity_relation_type ===
                    entityRelationType
                  }
                  setIsSelected={() => {
                    if (!isOptionDisabled) {
                      onFormChange({
                        entity_relation_type: entityRelationType,
                      });
                    }
                  }}
                  content={
                    <FieldDefinitionFormRelationTypeButtonContent
                      entityRelationType={entityRelationType}
                    />
                  }
                  noCheckbox
                  label={startCase(entityRelationType)}
                  isDisabled={isOptionDisabled}
                  disabledMessage={
                    isTemplate && isDisabled
                      ? 'Must select category first'
                      : undefined
                  }
                />
              </Flex>
            );
          },
        )}
      </Flex>
      {isTemplate && isDisabled && (
        <div
          style={{
            color: '#db2828',
            marginTop: '8px',
            fontSize: '0.9em',
          }}
        >
          Must select category first
        </div>
      )}

      {/* Preview section with example data */}
      <div style={{ marginTop: '20px' }}>
        <FieldDefinitionFormLinkTypePreview
          entityName={Resource.getHumanReadableEntityName(
            false,
            true,
          )}
          relationType={selectedRelationType}
          relationName={exampleData.relationName}
          backLinkName={exampleData.backLinkName}
          categoryNameA={exampleData.categoryA}
          categoryNameB={exampleData.categoryB}
          label="Example"
          resourceNameA={exampleData.resourceNameA}
          resourceNameB={exampleData.resourceNameB}
        />
      </div>
    </>
  );
};
